/**
 * Student Hub Feature Flags
 * 
 * Central configuration for enabling/disabling Student Hub modules.
 * Scoped ONLY to Student Hub — does not affect Auth, Wallet, Ads, Messaging, etc.
 * 
 * Each module defines:
 *   - enabled: whether the module is accessible
 *   - slug: URL-safe identifier
 *   - routes: { browse, search, submit } — standardized route patterns
 *   - legacyRoutes: backward-compatible routes that redirect to canonical ones
 *   - categoryKey: maps to backend API category
 */

const STUDENT_HUB_FEATURES = {
  books: {
    enabled: true,
    slug: 'books',
    categoryKey: 'books_notes',
    routes: {
      browse: '/student-hub/books',
      search: '/student-hub/books/search',
      submit: '/student-hub/books/new',
    },
    legacyRoutes: ['/student/books', '/student/books-notes'],
  },
  courses: {
    enabled: false, // DISABLED — hidden from UI, code preserved for future restoration
    slug: 'courses',
    categoryKey: 'courses_learning',
    routes: {
      browse: '/student-hub/courses',
      search: '/student-hub/courses/search',
      submit: '/student-hub/courses/new',
    },
    legacyRoutes: ['/student/courses', '/student/courses-skills'],
  },
  scholarships: {
    enabled: false, // DISABLED — hidden from UI, routes fall back to "Coming Soon" via FeatureGate
    slug: 'scholarships',
    categoryKey: 'scholarships',
    routes: {
      browse: '/student-hub/scholarships',
      search: '/student-hub/scholarships/search',
      submit: '/student-hub/scholarships/new',
    },
    legacyRoutes: ['/student/scholarships'],
  },
  internships: {
    enabled: false, // DISABLED — hidden from UI, routes fall back to "Coming Soon" via FeatureGate
    slug: 'internships',
    categoryKey: 'internships_jobs',
    routes: {
      browse: '/student-hub/internships',
      search: '/student-hub/internships/search',
      submit: '/student-hub/internships/new',
    },
    legacyRoutes: ['/student/internships', '/student/internships-jobs', '/student/opportunities'],
  },
  admissions: {
    enabled: false, // DISABLED — hidden from UI, code preserved for future restoration
    slug: 'admissions',
    categoryKey: 'universities_admissions',
    routes: {
      browse: '/student-hub/admissions',
      search: '/student-hub/admissions/search',
      submit: '/student-hub/admissions/new',
    },
    legacyRoutes: ['/student/admissions'],
  },
  cv: {
    enabled: true,
    slug: 'cv',
    categoryKey: 'cv_cover',
    routes: {
      browse: '/student-hub/cv',
      search: null, // CV Builder doesn't have a search flow
      submit: null, // CV Builder uses inline creation
    },
    legacyRoutes: ['/student/cv', '/student/cv-tools', '/student-hub/cv-builder'],
  },
};

/** Get feature config by slug */
export const getFeature = (slug) => STUDENT_HUB_FEATURES[slug] || null;

/** Check if a feature is enabled */
export const isFeatureEnabled = (slug) => STUDENT_HUB_FEATURES[slug]?.enabled ?? false;

/** Get all enabled features */
export const getEnabledFeatures = () =>
  Object.values(STUDENT_HUB_FEATURES).filter((f) => f.enabled);

/** Get all feature slugs */
export const getAllFeatureSlugs = () => Object.keys(STUDENT_HUB_FEATURES);

/** Get canonical browse route for a module */
export const getBrowseRoute = (slug) => STUDENT_HUB_FEATURES[slug]?.routes?.browse || null;

/** Get canonical submit route for a module */
export const getSubmitRoute = (slug) => STUDENT_HUB_FEATURES[slug]?.routes?.submit || null;

/** Map a legacy/old route slug to feature slug */
const LEGACY_SLUG_MAP = {
  'books-notes': 'books',
  'courses-skills': 'courses',
  'internships-jobs': 'internships',
  'cv-tools': 'cv',
  'opportunities': 'internships',
};

export const resolveFeatureSlug = (rawSlug) => LEGACY_SLUG_MAP[rawSlug] || rawSlug;

export default STUDENT_HUB_FEATURES;
